import { FC, useCallback, useEffect, useState } from 'react';
import { Product, UserWorkflow, Workflow, WORKFLOW_ACTIONS } from '@typings/api-models';
import CheckCircle from '@components/Icons/CheckCircle';
import Collaborators from '@lib/UserWorkflowDetail/Collaborators';
import useNoodleApi from '@hooks/useNoodleApi';
import { getUserWorkflowCollaborators } from '@tsClient/workflows';
import { FullWorkflowStep } from '@noodleApi/ApiDefinition/workflows';
import * as tsClient from '@tsClient';
import isStepCompleted from '@helpers/isStepCompleted';
import canRetriggerStep from '@helpers/canRetriggerStep';
import CircularProgress from '@components/CircularProgress';
import { useUser } from '@hooks';
import * as format from '@format';
import DocumentsAction from './DocumentsAction';
import classes from './UserWorkflowInfo.module.scss';

type UserWorkflowInfoProps = {
  compiledDocuments: Parameters<typeof DocumentsAction>[0]['compiledDocuments'] | null;
  userWorkflow: Pick<UserWorkflow, 'id' | 'notes' | 'numTotalTasks' | 'numCompletedTasks'> & {
    workflow: Pick<Workflow, 'id' | 'name'> & {
      creator: {
        id: string;
        slug: string;
      };
    };
    product?: Pick<Product, 'id' | 'title'> | null;
  };
  hasBorder: boolean;
  collaborators?: Parameters<typeof Collaborators>[0]['collaborators'];
  workflowSteps: FullWorkflowStep[];
  isCreatorView: boolean;
  onTrigger?: () => Promise<void>;
  isCard?: boolean;
};

const UserWorkflowInfo: FC<UserWorkflowInfoProps> = ({
  compiledDocuments,
  userWorkflow,
  collaborators,
  hasBorder,
  workflowSteps,
  isCreatorView,
  isCard,
  onTrigger,
}) => {
  const creatorSlug = userWorkflow.workflow.creator.slug;
  const [workflowCollaborators, setWorkflowCollaborators] = useState<UserWorkflowInfoProps['collaborators']>(collaborators);
  const [user] = useUser();
  const { data: loadedCollaborators, getData: getCollaboratorsFn } = useNoodleApi(getUserWorkflowCollaborators);
  const { getData: triggerUserWorkflowCTAFn } = useNoodleApi(tsClient.triggerUserWorkflowCTA);

  const handleAddCollaborator = useCallback((): void => {
    getCollaboratorsFn({ userWorkflowId: userWorkflow.id });
  }, [getCollaboratorsFn, userWorkflow.id]);

  const handleChangeCollaborator = useCallback((collaborator: Parameters<typeof Collaborators>[0]['collaborators'][number]): void => {
    setWorkflowCollaborators(prev => prev?.map(c => (c.id === collaborator.id ? collaborator : c)));
  }, []);

  const triggerWorkflowCTA = async (stepId: string): Promise<void> => {
    await triggerUserWorkflowCTAFn({ stepId, userWorkflowId: userWorkflow.id });
    await onTrigger?.();
  };

  useEffect(() => {
    if (loadedCollaborators) {
      setWorkflowCollaborators(loadedCollaborators);
    }
  }, [loadedCollaborators]);

  useEffect(() => {
    if (userWorkflow.id) {
      getCollaboratorsFn({ userWorkflowId: userWorkflow.id });
    }
  }, [userWorkflow.id, getCollaboratorsFn]);

  const hasNotes = Boolean(userWorkflow.notes);
  const hasTitle = Boolean(userWorkflow.product?.title);
  const hasDocuments = Boolean(compiledDocuments);
  const generatePdfsTrigger = workflowSteps?.find(wfStep =>
    wfStep.nextSteps.some(nextStep => nextStep.eventName === WORKFLOW_ACTIONS.GENERATE_ALL_WORKFLOW_PDFS),
  );
  const showGeneratePdfsButton = generatePdfsTrigger && isCreatorView
    && (!isStepCompleted(generatePdfsTrigger) || canRetriggerStep(generatePdfsTrigger));
  const showDocumentsDivider = (hasNotes || hasTitle) && hasDocuments;

  const isUserProfileDetails = !isCreatorView && !isCard;
  const myCollaborator = workflowCollaborators?.find(c => user && c.id === user.id) || null;

  return (
    <div className={hasBorder ? classes.workflowInfo_border : classes.workflowInfo}>
      <div className={classes.stepInfo}>
        <div
          className={classes.workflowInfo_text}
          style={
            (isUserProfileDetails && { margin: 0, marginBottom: 12 })
            || (!isCreatorView && isCard && { margin: 12, maxWidth: 'calc(100% - 80px)' })
            || undefined
          }
        >
          <p className={isCreatorView || isCard ? 'body-sm-bold' : 'heading-sm'}>
            {userWorkflow.workflow.name}
            {userWorkflow.notes && <>: {userWorkflow.notes}</>}
          </p>
          <p className={classes.tasksProgress}>
            {userWorkflow.numCompletedTasks < userWorkflow.numTotalTasks
              ? (
                <CircularProgress
                  size={12}
                  progress={format.percentageComplete({
                    completed: userWorkflow.numCompletedTasks,
                    total: userWorkflow.numTotalTasks,
                  })}
                />
              )
              : (
                <CheckCircle weight="fill" color="var(--color-success)" size={12} />
              )}
            {userWorkflow.numCompletedTasks} of {userWorkflow.numTotalTasks} tasks completed
          </p>
        </div>
      </div>
      {(userWorkflow.notes || userWorkflow.product?.title) && (isCreatorView || isCard) && (
        <>
          <div className={classes.border} />
          <div className={classes.workflowExtraInfo}>
            {userWorkflow.product?.title && (
              <div className={classes.workflowExtraInfo_text}>
                <p className={classes['workflow-name']}>Product</p>
                <p className="caption">{userWorkflow.product.title}</p>
              </div>
            )}
            {showDocumentsDivider && (compiledDocuments || showGeneratePdfsButton) && <div className={classes.divider} />}
            {(compiledDocuments || showGeneratePdfsButton) && (
              <>
                <DocumentsAction
                  creatorSlug={creatorSlug}
                  compiledDocuments={compiledDocuments || []}
                  userWorkflowId={userWorkflow.id}
                  onGeneratePdfs={triggerWorkflowCTA}
                  generatePdfsTrigger={showGeneratePdfsButton ? generatePdfsTrigger : null}
                />
              </>
            )}
          </div>
        </>
      )}
      {workflowCollaborators && myCollaborator && !isCard && (
        <>
          {isCreatorView && <div className={classes.border} />}
          <Collaborators
            collaborators={workflowCollaborators}
            userWorkflow={userWorkflow}
            myCollaborator={myCollaborator}
            onAddCollaborators={handleAddCollaborator}
            onChangeCollaborator={handleChangeCollaborator}
            isUserProfileDetails={isUserProfileDetails}
          />
        </>
      )}
    </div>
  );
};

export default UserWorkflowInfo;
